import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { settingsApi, SystemSetting } from '@/api/settings';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Textarea } from '../../ui/textarea';

interface SettingEditorProps {
  setting: SystemSetting;
  onCancel: () => void;
  onSave: () => void;
}

function isRegistrationDeadlineSetting(key: string): boolean {
  return key === 'REGISTRATION_ATTENDEE_DEADLINE' || key === 'REGISTRATION_SPEAKER_DEADLINE';
}

function formatDateTimeLocal(value: unknown): string {
  if (!value) return '';
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function parseJsonIfNeeded(raw: string): unknown {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  try {
    return JSON.parse(trimmed);
  } catch {
    return raw;
  }
}

export function SettingEditor({ setting, onCancel, onSave }: SettingEditorProps) {
  const queryClient = useQueryClient();
  const [value, setValue] = useState<unknown>(setting.value);

  const isCriticalToggle = useMemo(
    () => setting.key === 'system.maintenance.mode',
    [setting.key]
  );

  const mutation = useMutation({
    mutationFn: async (newValue: unknown) =>
      settingsApi.updateSetting(setting.key, {
        scope: setting.scope,
        editionId: setting.editionId,
        eventId: setting.eventId,
        value: newValue,
        type: setting.type,
        description: setting.description ?? undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      toast.success('Configuracion actualizada.');
      onSave();
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Error desconocido';
      toast.error(`No fue posible actualizar: ${message}`);
    },
  });

  const handleSave = () => {
    if (
      isCriticalToggle &&
      setting.type === 'BOOLEAN' &&
      Boolean(setting.value) !== Boolean(value) &&
      !window.confirm('Vas a cambiar el modo mantenimiento. ¿Deseas continuar?')
    ) {
      return;
    }
    mutation.mutate(value);
  };

  const renderInput = () => {
    if (setting.type === 'BOOLEAN') {
      return (
        <div className="flex items-center justify-between rounded-lg border bg-muted/50 px-4 py-3">
          <Label className="text-sm font-medium text-slate-700">
            {Boolean(value) ? 'Activado' : 'Desactivado'}
          </Label>
          <Switch
            className="!h-8 !w-14 !rounded-full !border !border-slate-300 data-[state=checked]:!bg-[#1e3a5f] data-[state=unchecked]:!bg-slate-300 [&_[data-slot=switch-thumb]]:!size-6 [&_[data-slot=switch-thumb]]:!rounded-full [&_[data-slot=switch-thumb]]:!bg-white [&_[data-slot=switch-thumb]]:shadow-sm [&_[data-slot=switch-thumb][data-state=unchecked]]:!translate-x-[2px] [&_[data-slot=switch-thumb][data-state=checked]]:!translate-x-[30px]"
            checked={Boolean(value)}
            onCheckedChange={(checked) => setValue(checked)}
          />
        </div>
      );
    }

    if (setting.type === 'NUMBER') {
      return (
        <Input
          type="number"
          className="max-w-[100px]"
          value={typeof value === 'number' ? value : Number(value ?? 0)}
          onChange={(event) => setValue(Number(event.target.value))}
        />
      );
    }

    if (setting.type === 'DATETIME') {
      return (
        <Input
          type="datetime-local"
          value={formatDateTimeLocal(value)}
          onChange={(event) => setValue(event.target.value)}
        />
      );
    }

    if (isRegistrationDeadlineSetting(setting.key)) {
      return (
        <div className="space-y-2">
          <Input
            type="datetime-local"
            value={formatDateTimeLocal(value)}
            onChange={(event) => {
              const nextValue = event.target.value;
              setValue(nextValue ? new Date(nextValue).toISOString() : '');
            }}
          />
          <p className="text-sm text-slate-500">
            La fecha se evaluará con horario local de México y se guardará en UTC.
          </p>
        </div>
      );
    }

    if (setting.type === 'JSON') {
      return (
        <Textarea
          value={typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
          onChange={(event) => setValue(parseJsonIfNeeded(event.target.value))}
          rows={6}
        />
      );
    }

    return (
      <Input
        type="text"
        value={value == null ? '' : String(value)}
        onChange={(event) => setValue(event.target.value)}
      />
    );
  };

  return (
    <div className="space-y-4">
      {renderInput()}
      <div className="flex items-center gap-3">
        <Button
          variant="outline"
          className="h-10 min-w-[104px] rounded-lg border-slate-300 bg-white text-slate-900 hover:bg-slate-50"
          onClick={onCancel}
          disabled={mutation.isPending}
        >
          Cancelar
        </Button>
        <Button
          className="h-10 min-w-[156px] rounded-lg bg-[#1e3a5f] text-white hover:bg-[#18314f]"
          onClick={handleSave}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Guardando...
            </>
          ) : (
            'Guardar Cambios'
          )}
        </Button>
      </div>
    </div>
  );
}
